import React, { useState, useEffect, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Box } from '@mui/material';
import { useAuth } from './contexts/AuthContext';
import usePageViewTracker from './hooks/usePageViewTracker';
import { apiService } from './services/apiService';
import Layout from './components/LayoutUnified';
import ImpersonationBanner from './components/ImpersonationBanner';
import ControlCenterLayout from './components/ControlCenterLayout';
import PrivateRoute from './components/common/PrivateRoute';
import AdminRoute from './components/common/AdminRoute';
import ServerAdminRoute from './components/common/ServerAdminRoute';
import ObservatoryAdminRoute from './components/common/ObservatoryAdminRoute';
import AdminOrObservatoryAdminRoute from './components/common/AdminOrObservatoryAdminRoute';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';

// User pages
import Dashboard from './pages/user/Dashboard';
import ObservationSubmit from './pages/user/ObservationSubmit';
import ObservationDetail from './pages/user/ObservationDetail';
import ObservationLogPage from './pages/user/ObservationLogPage';
import ObservationEdit from './pages/observations/ObservationEdit';
import ObservationTimeline from './components/ObservationTimeline';
import MultiDayTimeline from './components/MultiDayTimeline';
import ObservatoryList from './pages/user/ObservatoryList';
import ObservatoryDetail from './pages/user/ObservatoryDetailNew';
import Profile from './pages/user/Profile';
import ContactSupport from './pages/user/ContactSupport';
import ExoplanetTransits from './pages/exoplanets/ExoplanetTransitsPage';
import FileBrowser from './pages/files/FileBrowser';
import FileDetail from './pages/files/FileDetail';
import ProjectFiles from './pages/files/ProjectFiles';
import TargetFiles from './pages/files/TargetFiles';
import Library from './pages/user/Library';
import CalibrationLibrary from './pages/user/CalibrationLibrary';
import ActivityHistory from './pages/user/ActivityHistory';
import About from './pages/About';
import MyUsage from './pages/MyUsage';
import ObservationSearch from './pages/observations/ObservationSearch';
import TargetObservationReport from './pages/reports/TargetObservationReport';

// Unified pages
import ObservationList from './pages/observations/ObservationList';
import ObservationFiles from './pages/observations/ObservationFiles';
import ProjectDashboardSimple from './pages/projects/ProjectDashboardSimple';
import ProjectMemberManagement from './pages/projects/ProjectMemberManagement';
import ProjectFormStandalone from './pages/projects/ProjectFormStandalone';

import LoadingScreen from './components/common/LoadingScreen';
import { lazyWithRetry } from './utils/lazyWithRetry';

// Administrative pages (for observatory admins and org owners)
import MyObservatories from './pages/administrative/MyObservatories';
import AdministrativeObservations from './pages/administrative/AdministrativeObservations';
import MyAdminProjects from './pages/MyAdminProjects';

// Admin pages — lazy-loaded into a shared `admin` chunk (via the
// webpackChunkName magic comment) so the ~26 server-/observatory-admin-only
// pages are kept out of the user-gui main bundle. Regular users never fetch
// this chunk; admins pull it on first visit to any /admin route.
const AdminDashboard = lazyWithRetry(() => import(/* webpackChunkName: "admin" */ './pages/admin/AdminDashboard'));
const ObservatoryManagement = lazyWithRetry(() => import(/* webpackChunkName: "admin" */ './pages/admin/ObservatoryManagement'));
const ObservatoryEditPage = lazyWithRetry(() => import(/* webpackChunkName: "admin" */ './pages/admin/ObservatoryEditPage'));
const ObservatoryReservationsPage = lazyWithRetry(() => import(/* webpackChunkName: "admin" */ './pages/admin/observatory-reservations/ObservatoryReservationsPage'));
const ObservatoryDetails = lazyWithRetry(() => import(/* webpackChunkName: "admin" */ './pages/admin/ObservatoryDetailsNew'));
const ObservatoryMemberManagement = lazyWithRetry(() => import(/* webpackChunkName: "admin" */ './pages/admin/ObservatoryMemberManagement'));
const ObservatoryMembershipTree = lazyWithRetry(() => import(/* webpackChunkName: "admin" */ './pages/admin/ObservatoryMembershipTree'));
const OrganizationMembershipTree = lazyWithRetry(() => import(/* webpackChunkName: "admin" */ './pages/admin/OrganizationMembershipTree'));
const ProjectMembershipTree = lazyWithRetry(() => import(/* webpackChunkName: "admin" */ './pages/projects/ProjectMembershipTree'));
const SessionsView = lazyWithRetry(() => import(/* webpackChunkName: "admin" */ './pages/admin/SessionsView'));
const ObservatoryHistoryView = lazyWithRetry(() => import(/* webpackChunkName: "admin" */ './pages/admin/ObservatoryHistoryView'));
const UserObservatoryHistoryView = lazyWithRetry(() => import(/* webpackChunkName: "admin" */ './pages/user/ObservatoryHistoryView'));
const ProjectHistoryView = lazyWithRetry(() => import(/* webpackChunkName: "admin" */ './pages/admin/ProjectHistoryView'));
const OrganizationHistoryView = lazyWithRetry(() => import(/* webpackChunkName: "admin" */ './pages/admin/OrganizationHistoryView'));
const UserProjectHistoryView = lazyWithRetry(() => import(/* webpackChunkName: "admin" */ './pages/projects/ProjectHistoryView'));
const UserManagement = lazyWithRetry(() => import(/* webpackChunkName: "admin" */ './pages/admin/UserManagement'));
const UsersFullView = lazyWithRetry(() => import(/* webpackChunkName: "admin" */ './pages/admin/UsersFullView'));
const AdminUserActivity = lazyWithRetry(() => import(/* webpackChunkName: "admin" */ './pages/admin/AdminUserActivity'));
// POC: realtime — standalone real-time observing page (its own chunk; auth = the launched URL)
const RealtimePage = lazyWithRetry(() => import(/* webpackChunkName: "realtime" */ './pages/realtime/RealtimePage'));
const RtDevLauncher = lazyWithRetry(() => import(/* webpackChunkName: "realtime" */ './pages/realtime/RtDevLauncher'));
const UserAccessTree = lazyWithRetry(() => import(/* webpackChunkName: "admin" */ './pages/admin/UserAccessTree'));
const AnalyticsPaneStandalone = lazyWithRetry(() => import(/* webpackChunkName: "admin" */ './pages/admin/AnalyticsPaneStandalone'));
const OrganizationManagement = lazyWithRetry(() => import(/* webpackChunkName: "admin" */ './pages/admin/OrganizationManagement'));
const ApiKeyManagement = lazyWithRetry(() => import(/* webpackChunkName: "admin" */ './pages/admin/ApiKeyManagement'));
const Analytics = lazyWithRetry(() => import(/* webpackChunkName: "admin" */ './pages/admin/Analytics'));
const SystemSettings = lazyWithRetry(() => import(/* webpackChunkName: "admin" */ './pages/admin/SystemSettings'));
const DatabaseIntegrity = lazyWithRetry(() => import(/* webpackChunkName: "admin" */ './pages/admin/DatabaseIntegrity'));
const BackupManagement = lazyWithRetry(() => import(/* webpackChunkName: "admin" */ './pages/admin/BackupManagement'));
const SystemStatus = lazyWithRetry(() => import(/* webpackChunkName: "admin" */ './pages/admin/SystemStatus'));
const SystemMotdManagement = lazyWithRetry(() => import(/* webpackChunkName: "admin" */ './pages/admin/SystemMotdManagement'));
const LogViewer = lazyWithRetry(() => import(/* webpackChunkName: "admin" */ './pages/admin/LogViewer'));
const SmartLogViewer = lazyWithRetry(() => import(/* webpackChunkName: "admin" */ './pages/admin/SmartLogViewer'));
const LogSettings = lazyWithRetry(() => import(/* webpackChunkName: "admin" */ './pages/admin/LogSettings'));
const PluginReleaseManagement = lazyWithRetry(() => import(/* webpackChunkName: "admin" */ './pages/admin/PluginReleaseManagement'));
const AdminCalibrationLibrary = lazyWithRetry(() => import(/* webpackChunkName: "admin" */ './pages/admin/AdminCalibrationLibrary'));
const CalibrationSettings = lazyWithRetry(() => import(/* webpackChunkName: "admin" */ './pages/admin/CalibrationSettings'));

// Billing & Reports pages — lazy-loaded into a shared `billing` chunk
// (server-admin-only), likewise kept out of the main bundle.
const UsageDashboard = lazyWithRetry(() => import(/* webpackChunkName: "billing" */ './pages/admin/billing/UsageDashboard'));
const UserUsageReport = lazyWithRetry(() => import(/* webpackChunkName: "billing" */ './pages/admin/billing/UserUsageReport'));
const ProjectUsageReport = lazyWithRetry(() => import(/* webpackChunkName: "billing" */ './pages/admin/billing/ProjectUsageReport'));
const ObservatoryUtilization = lazyWithRetry(() => import(/* webpackChunkName: "billing" */ './pages/admin/billing/ObservatoryUtilization'));
const UsageExplorer = lazyWithRetry(() => import(/* webpackChunkName: "billing" */ './pages/admin/billing/UsageExplorer'));

// Lazy-load the Admin Control Center — its dependency graph (recharts,
// MUI x-date-pickers, all cards/panes, embedded SystemStatus) is heavy and
// irrelevant for users who never visit /admin/control-center.
const ControlCenter = lazyWithRetry(() => import('./pages/admin/control-center/ControlCenter'));

// Service Center — server-admin-only support/service tools. Lazy-loaded so its
// chunk is fetched only when a server admin opens it.
const ServiceCenter = lazyWithRetry(() => import('./pages/admin/service-center/ServiceCenter'));
const PlanObservationPage = lazyWithRetry(() => import('./pages/operator/PlanObservationPage'));

// Operator (Mission Control) pages — lazy-loaded as a single `operator`
// chunk via the shared `webpackChunkName` magic comment below. The
// operator tree is a heavy dependency graph (OperatorLayout + eight
// route views + AdminDrawer + CommandPalette + telemetry/floating-window
// helpers + chart bits) that is irrelevant on first paint for users
// who land anywhere outside /observatory/:id or /operations.
const OperatorLayout = lazyWithRetry(() =>
  import(/* webpackChunkName: "operator" */ './components/operator/OperatorLayout')
);
const OperationsDashboard = lazyWithRetry(() =>
  import(/* webpackChunkName: "operator" */ './pages/operator/OperationsDashboard')
);
const ScheduleView = lazyWithRetry(() =>
  import(/* webpackChunkName: "operator" */ './pages/operator/ScheduleView')
);
const WeatherView = lazyWithRetry(() =>
  import(/* webpackChunkName: "operator" */ './pages/operator/WeatherView')
);
const ObservatoryView = lazyWithRetry(() =>
  import(/* webpackChunkName: "operator" */ './pages/operator/ObservatoryView')
);
const PlanetariumView = lazyWithRetry(() =>
  import(/* webpackChunkName: "operator" */ './pages/operator/PlanetariumView')
);
const AdministrationView = lazyWithRetry(() =>
  import(/* webpackChunkName: "operator" */ './pages/operator/AdministrationView')
);
const EmbeddedObservationDetail = lazyWithRetry(() =>
  import(/* webpackChunkName: "operator" */ './pages/operator/EmbeddedObservationDetail')
);
// Pop-out from the admin Control Center's Schedule Analytics pane.
// Pulls in TimelineView (lazy-imported from operator chunk inside the
// page itself), so the user-gui main bundle isn't affected.
const ScheduleAnalyticsExpanded = lazyWithRetry(() =>
  import('./pages/admin/ScheduleAnalyticsExpanded')
);
// Pop-out world map of observatories, opened in a new browser tab from
// the admin Control Center's ObservatoriesCard. Lazy so Google Maps +
// clusterer aren't pulled into the main bundle.
const ObservatoriesMapPage = lazyWithRetry(() =>
  import('./pages/admin/control-center/ObservatoriesMapPage')
);

// Redirect to the user's preferred default view
const DefaultViewRedirect = () => {
  const { user } = useAuth();
  const defaultView = user?.preferences?.default_view || 'classic_user';
  const defaultObsId = user?.preferences?.default_observatory_id;
  const needsLookup = defaultView === 'operations_control' && !defaultObsId;
  const [resolvedObsId, setResolvedObsId] = useState(null);
  const [loading, setLoading] = useState(needsLookup);

  // If operations_control but no default observatory, find the first one the user has access to
  useEffect(() => {
    if (!needsLookup) return;
    const userId = user?.id || user?._id;
    if (!userId) { setLoading(false); return; }
    apiService.getUserMemberships(userId).then(response => {
      const memberships = response?.success ? (response.data || []) : (response || []);
      if (memberships.length > 0) {
        setResolvedObsId(memberships[0].observatory_id || memberships[0]._id);
      }
    }).catch(() => {}).finally(() => setLoading(false));
  }, [needsLookup, user]);

  if (defaultView === 'classic_admin') {
    return <Navigate to="/admin/dashboard" replace />;
  }
  if (defaultView === 'control_center') {
    return <Navigate to="/admin/control-center" replace />;
  }
  if (defaultView === 'service_center') {
    return <Navigate to="/admin/service-center" replace />;
  }
  if (defaultView === 'operations_control') {
    const obsId = defaultObsId || resolvedObsId;
    if (obsId) return <Navigate to={`/observatory/${obsId}/operations`} replace />;
    if (loading) return null; // Wait for observatory lookup
    // User prefers operations_control but has no observatory access yet.
    // Honour the preference by dropping them into the observatory-less
    // operations view (active obs / queue / past) rather than falling
    // through to the legacy classic-user dashboard.
    return <Navigate to="/operations" replace />;
  }
  // classic_user or fallback
  return <Navigate to="/dashboard" replace />;
};

function App() {
  const { loading } = useAuth();
  // Mounted here (above every layout) so route changes are beaconed for ALL pages — Service Center and
  // Control Center render under their own layouts, and Operations under yet another, not LayoutUnified.
  usePageViewTracker();

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <ImpersonationBanner />
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />

        {/* Protected routes */}
        <Route element={<PrivateRoute />}>
          {/* POC: realtime — real-time observing session. Requires login; the controller/spectator role
              still comes from the launched URL. The session link survives the login round-trip (PrivateRoute
              preserves the path + query, and Login redirects back to it). */}
          <Route path="/rt" element={<Suspense fallback={<LoadingScreen />}><RealtimePage /></Suspense>} />
          <Route path="/rt/dev" element={<Suspense fallback={<LoadingScreen />}><RtDevLauncher /></Suspense>} />
          {/* Operator (Mission Control) layout - uses :observatoryId for compatibility with ObservationTimeline.
              Wrapped in a single Suspense so the nested route views inherit the fallback while the operator chunk fetches. */}
          <Route path="/observatory/:observatoryId" element={
            <Suspense fallback={<LoadingScreen />}>
              <OperatorLayout />
            </Suspense>
          }>
            <Route index element={<Navigate to="operations" replace />} />
            <Route path="operations" element={<OperationsDashboard />} />
            <Route path="schedule" element={<ScheduleView />} />
            <Route path="weather" element={<WeatherView />} />
            <Route path="observatory" element={<ObservatoryView />} />
            <Route path="planetarium" element={<PlanetariumView />} />
            <Route path="administration" element={<AdministrationView />} />
          </Route>

          {/* Operations view for users with no observatory access — their
              preferences point at operations_control but they have nothing
              to select. OperatorLayout handles undefined observatoryId by
              stripping observatory-specific UI (state/equipment line,
              Schedule/Weather/Observatory/Planetarium/Administration
              tabs, right-column panels). */}
          <Route path="/operations" element={
            <Suspense fallback={<LoadingScreen />}>
              <OperatorLayout />
            </Suspense>
          }>
            <Route index element={<OperationsDashboard />} />
          </Route>

          {/* Embedded observation detail — tabbed view for floating detail window */}
          <Route path="/observations/:id/embedded" element={
            <Suspense fallback={<LoadingScreen />}>
              <EmbeddedObservationDetail />
            </Suspense>
          } />

          {/* Full event log — opened in a new tab from the observation detail
              page's ObservationLog "open in new tab" action. No layout chrome
              (no menu bar / breadcrumbs) — a focused pop-out. */}
          <Route path="/observations/:id/log" element={<ObservationLogPage />} />

          {/* Plan Observation in its own tab — the floating window's "New tab" moves the
              conversation here (?observatory=<id>&session=<id>). No layout chrome. */}
          <Route path="/plan-observation" element={
            <Suspense fallback={<LoadingScreen />}>
              <PlanObservationPage />
            </Suspense>
          } />

          {/* Schedule Analytics expanded — opened in a new tab from the
              admin Control Center. Each accessible observatory is
              rendered with the same TimelineView used by the operator
              Schedule tab. No layout chrome — it's a focused pop-out. */}
          <Route path="/admin/schedule-analytics" element={
            <Suspense fallback={<LoadingScreen />}>
              <ScheduleAnalyticsExpanded />
            </Suspense>
          } />

          {/* About — entered from OperatorLayout, ControlCenterLayout, and
              LayoutUnified brand-click; renders standalone (no surrounding
              chrome) so the legacy left drawer doesn't bleed in when reached
              from the modern layouts. Browser back returns to the origin. */}
          <Route path="/about" element={<About />} />

          {/* Admin Control Center uses its own minimal layout (no legacy nav drawer)
              and is lazy-loaded so its chunk is fetched only when an admin
              actually opens it. */}
          <Route element={<ControlCenterLayout />}>
            <Route element={<AdminRoute />}>
              <Route
                path="/admin/control-center"
                element={
                  <Suspense fallback={<LoadingScreen />}>
                    <ControlCenter />
                  </Suspense>
                }
              />
            </Route>
            {/* Service Center — strictly server-admin only. */}
            <Route element={<ServerAdminRoute />}>
              <Route
                path="/admin/service-center"
                element={
                  <Suspense fallback={<LoadingScreen />}>
                    <ServiceCenter />
                  </Suspense>
                }
              />
            </Route>
          </Route>

          {/* Classic layout */}
          <Route element={<Layout />}>
            {/* User routes */}
            <Route path="/" element={<DefaultViewRedirect />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/observations/new" element={<ObservationSubmit />} />
            <Route path="/observations" element={<ObservationList mode="user" />} />
            <Route path="/observations/:id/edit" element={<ObservationEdit />} />
            <Route path="/observations/:id" element={<ObservationDetail />} />
            <Route path="/observations/search" element={<ObservationSearch />} />
            <Route path="/observations/:id/files" element={<ObservationFiles />} />
            <Route path="/projects" element={<ProjectDashboardSimple mode="user" />} />
            <Route path="/projects/new" element={<ProjectFormStandalone mode="create" />} />
            <Route path="/projects/:id/edit" element={<ProjectFormStandalone mode="edit" />} />
            <Route path="/projects/:id/members" element={<ProjectMemberManagement />} />
            <Route path="/projects/:id/membership-tree" element={<ProjectMembershipTree />} />
            <Route path="/projects/:id/history" element={<Suspense fallback={<LoadingScreen />}><UserProjectHistoryView /></Suspense>} />
            <Route path="/organizations/:id/membership-tree" element={<OrganizationMembershipTree />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/exoplanets" element={<ExoplanetTransits />} />
            <Route path="/files" element={<FileBrowser />} />
            <Route path="/files/project/:projectId" element={<ProjectFiles />} />
            <Route path="/files/target/:targetName" element={<TargetFiles />} />
            <Route path="/files/file/:fileId" element={<FileDetail />} />
            <Route path="/observatories" element={<ObservatoryList />} />
            <Route path="/observatories/:id" element={<ObservatoryDetail />} />
            <Route path="/observatories/:id/history" element={<Suspense fallback={<LoadingScreen />}><UserObservatoryHistoryView /></Suspense>} />
            <Route path="/library" element={<Library />} />
            <Route path="/calibration/library" element={<CalibrationLibrary />} />
            <Route path="/activity-history" element={<ActivityHistory />} />
            <Route path="/my-usage" element={<MyUsage />} />
            <Route path="/reports/targets" element={<TargetObservationReport />} />
            <Route path="/support" element={<ContactSupport />} />

            {/* Administrative routes - for observatory admins */}
            <Route path="/administrative/observatories" element={<MyObservatories />} />
            <Route path="/administrative/observations" element={<AdministrativeObservations />} />
            <Route path="/administrative/organizations" element={<OrganizationManagement mode="owner" />} />
            <Route path="/administrative/projects" element={<MyAdminProjects />} />
            <Route path="/administrative/users" element={<UserManagement mode="restricted" />} />

            {/* Observatory-specific admin routes - accessible to server admins and observatory admins */}
            <Route element={<ObservatoryAdminRoute />}>
              <Route path="/admin/observatories/:id" element={<ObservatoryDetails />} />
              <Route path="/admin/observatories/:id/users" element={<ObservatoryMemberManagement />} />
              <Route path="/admin/observatories/:id/membership-tree" element={<ObservatoryMembershipTree />} />
              <Route path="/admin/observatories/:id/sessions" element={<SessionsView />} />
              <Route path="/admin/observatories/:id/history" element={<ObservatoryHistoryView />} />
              <Route path="/admin/observatories/:id/reservations" element={<ObservatoryReservationsPage />} />
            </Route>

            {/* Timeline routes - accessible to all authenticated users */}
            <Route path="/timeline/:observatoryId" element={<ObservationTimeline />} />
            <Route path="/multi-day-timeline/:observatoryId" element={<MultiDayTimeline />} />

            {/* Log Viewer - accessible to server admins AND observatory admins/owners */}
            <Route element={<AdminOrObservatoryAdminRoute />}>
              <Route path="/admin/logs" element={<LogViewer />} />
              <Route path="/admin/logs/smart-viewer" element={<SmartLogViewer />} />
            </Route>

            {/* Admin routes - protected by AdminRoute component (server admins only) */}
            <Route element={<AdminRoute />}>
              <Route path="/admin/dashboard" element={<AdminDashboard />} />
              <Route path="/admin/observatories" element={<ObservatoryManagement />} />
              <Route path="/admin/observatories/:id/edit" element={<ObservatoryEditPage />} />
              <Route path="/admin/observations" element={<ObservationList mode="admin" />} />
              <Route path="/admin/projects" element={<ProjectDashboardSimple mode="admin" />} />
              <Route path="/admin/projects/:id/history" element={<ProjectHistoryView />} />
              <Route path="/admin/users" element={<UserManagement />} />
              <Route path="/admin/users-full" element={<UsersFullView />} />
              <Route path="/admin/users/:userId/activity" element={<AdminUserActivity />} />
              <Route path="/admin/users/:userId/access-tree" element={<UserAccessTree />} />
              <Route path="/admin/analytics-pane" element={<AnalyticsPaneStandalone />} />
              <Route
                path="/admin/observatories/map"
                element={(
                  <Suspense fallback={<LoadingScreen />}>
                    <ObservatoriesMapPage />
                  </Suspense>
                )}
              />
              <Route path="/admin/api-keys" element={<ApiKeyManagement />} />
              <Route path="/admin/analytics" element={<Analytics />} />
              <Route path="/admin/settings" element={<SystemSettings />} />
              <Route path="/admin/status" element={<SystemStatus />} />
              <Route path="/admin/announcements" element={<SystemMotdManagement />} />
              <Route path="/admin/integrity" element={<DatabaseIntegrity />} />
              <Route path="/admin/backups" element={<BackupManagement />} />
              <Route path="/admin/organizations" element={<OrganizationManagement />} />
              <Route path="/admin/organizations/:id/history" element={<OrganizationHistoryView />} />
              <Route path="/admin/logs/settings" element={<LogSettings />} />
              <Route path="/admin/plugin-releases" element={<PluginReleaseManagement />} />
              <Route path="/admin/calibration/library" element={<AdminCalibrationLibrary />} />
              <Route path="/admin/calibration/settings/:observatoryId" element={<CalibrationSettings />} />

              {/* Billing & Reports routes */}
              <Route path="/admin/billing/dashboard" element={<UsageDashboard />} />
              <Route path="/admin/billing/explorer" element={<UsageExplorer />} />
              <Route path="/admin/billing/users" element={<UserUsageReport />} />
              <Route path="/admin/billing/projects" element={<ProjectUsageReport />} />
              <Route path="/admin/billing/observatories" element={<ObservatoryUtilization />} />
            </Route>
          </Route>
        </Route>
        
        {/* Catch all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Box>
  );
}

export default App;
